import React, { useState, useEffect, useCallback } from 'react';
import {
  TrendingUp, TrendingDown, DollarSign, ShoppingCart, Users, Target,
  Award, AlertTriangle, RefreshCw, Download, Zap, Package, Calendar,
  CreditCard, PieChart, BarChart3, Layers, ArrowUpRight, ArrowDownRight
} from 'lucide-react';
import reportService from '../../services/reportService';

/**
 * PAGE DE REPORTING OPTIMISÉE — 8 rubriques
 * 1 seul appel API, sélecteur de période, barres visuelles, growth calculé
 */

// ══════════════════════════════
// STYLES CSS INTÉGRÉS
// ══════════════════════════════
const rpStyles = `
/* ── Layout ── */
.rp-page { padding: 1rem; max-width: 1400px; margin: 0 auto; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; color: #1E293B; }
.rp-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 1rem; flex-wrap: wrap; gap: 0.5rem; }
.rp-header h3 { margin: 0; font-size: 1.4rem; font-weight: 700; color: #0F172A; }
.rp-subtitle { color: #64748B; font-size: 0.85rem; margin: 0.15rem 0 0; }
.rp-actions { display: flex; gap: 0.5rem; }

/* ── Period Selector ── */
.rp-period-selector { margin-bottom: 1rem; }
.rp-period-buttons { display: flex; gap: 0.35rem; flex-wrap: wrap; }
.rp-period-btn { display: inline-flex; align-items: center; gap: 0.3rem; padding: 0.4rem 0.8rem; border: 1px solid #E2E8F0; border-radius: 0.5rem; background: #fff; font-size: 0.8rem; cursor: pointer; color: #475569; transition: all 0.15s; font-weight: 500; }
.rp-period-btn:hover { border-color: #3B82F6; color: #3B82F6; }
.rp-period-btn.active { background: #3B82F6; color: #fff; border-color: #3B82F6; }
.rp-custom-range { display: flex; align-items: center; gap: 0.5rem; margin-top: 0.5rem; flex-wrap: wrap; }
.rp-date-input { padding: 0.35rem 0.5rem; border: 1px solid #E2E8F0; border-radius: 0.4rem; font-size: 0.8rem; }

/* ── Buttons ── */
.rp-btn-primary { padding: 0.4rem 0.8rem; background: #3B82F6; color: #fff; border: none; border-radius: 0.4rem; font-size: 0.8rem; cursor: pointer; font-weight: 500; }
.rp-btn-primary:hover { background: #2563EB; }
.rp-btn-sm { padding: 0.3rem 0.6rem; font-size: 0.75rem; }
.rp-btn-outline { display: inline-flex; align-items: center; gap: 0.3rem; padding: 0.4rem 0.8rem; border: 1px solid #E2E8F0; border-radius: 0.5rem; background: #fff; font-size: 0.8rem; cursor: pointer; color: #475569; font-weight: 500; }
.rp-btn-outline:hover { border-color: #3B82F6; color: #3B82F6; }
.rp-link-btn { background: none; border: none; color: #3B82F6; font-weight: 600; cursor: pointer; margin-left: 0.5rem; font-size: 0.8rem; }

/* ── Tabs ── */
.rp-tabs { display: flex; gap: 0.2rem; border-bottom: 2px solid #E2E8F0; margin-bottom: 1rem; overflow-x: auto; padding-bottom: 0; }
.rp-tab { display: inline-flex; align-items: center; gap: 0.3rem; padding: 0.6rem 0.8rem; border: none; background: none; font-size: 0.8rem; color: #64748B; cursor: pointer; border-bottom: 2px solid transparent; margin-bottom: -2px; white-space: nowrap; font-weight: 500; transition: all 0.15s; }
.rp-tab:hover { color: #3B82F6; }
.rp-tab.active { color: #3B82F6; border-bottom-color: #3B82F6; font-weight: 600; }
.rp-tab-badge { background: #EF4444; color: #fff; border-radius: 10px; padding: 0.1rem 0.4rem; font-size: 0.65rem; font-weight: 700; margin-left: 0.2rem; }
.rp-tab-count { color: #94A3B8; font-size: 0.75rem; }

/* ── Stats Grid (KPIs) ── */
.rp-stats-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(210px, 1fr)); gap: 0.75rem; margin-bottom: 1rem; }
.rp-stat-card { background: #fff; border-radius: 0.75rem; padding: 1rem; border: 1px solid #E2E8F0; box-shadow: 0 1px 3px rgba(0,0,0,0.04); }
.rp-stat-header { display: flex; justify-content: space-between; align-items: flex-start; }
.rp-stat-title { font-size: 0.75rem; color: #64748B; font-weight: 500; margin-bottom: 0.3rem; text-transform: uppercase; letter-spacing: 0.3px; }
.rp-stat-value { font-size: 1.35rem; font-weight: 700; color: #0F172A; line-height: 1.2; }
.rp-stat-growth { display: inline-flex; align-items: center; gap: 0.2rem; font-size: 0.72rem; font-weight: 600; margin-top: 0.3rem; padding: 0.1rem 0.4rem; border-radius: 4px; }
.rp-stat-growth.pos { color: #059669; background: #ECFDF5; }
.rp-stat-growth.neg { color: #DC2626; background: #FEF2F2; }
.rp-stat-sub { font-size: 0.72rem; color: #64748B; margin-top: 0.2rem; }
.rp-stat-icon { width: 48px; height: 48px; border-radius: 12px; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }

/* ── Cards ── */
.rp-card { background: #fff; border-radius: 0.75rem; border: 1px solid #E2E8F0; box-shadow: 0 1px 3px rgba(0,0,0,0.04); overflow: hidden; margin-bottom: 1rem; }
.rp-card-head { display: flex; justify-content: space-between; align-items: center; padding: 0.75rem 1rem; border-bottom: 1px solid #F1F5F9; }
.rp-card-head h6 { margin: 0; font-size: 0.85rem; font-weight: 600; display: flex; align-items: center; gap: 0.4rem; color: #0F172A; }
.rp-card-total { font-size: 0.75rem; color: #64748B; font-weight: 500; }
.rp-card-body { padding: 1rem; }

/* ── Bar Chart ── */
.rp-bar-chart { display: flex; align-items: flex-end; gap: 4px; height: 220px; padding-top: 1.5rem; overflow-x: auto; }
.rp-bar-col { display: flex; flex-direction: column; align-items: center; flex: 1; min-width: 36px; max-width: 70px; height: 100%; }
.rp-bar-val { font-size: 0.6rem; color: #64748B; margin-bottom: 4px; white-space: nowrap; transform: rotate(-30deg); transform-origin: bottom center; }
.rp-bar-wrap { flex: 1; width: 100%; display: flex; align-items: flex-end; }
.rp-bar-fill { width: 100%; border-radius: 4px 4px 0 0; min-height: 2px; position: relative; transition: height 0.4s ease; }
.rp-bar-count { position: absolute; top: 4px; left: 50%; transform: translateX(-50%); font-size: 0.55rem; color: #fff; font-weight: 700; }
.rp-bar-label { font-size: 0.65rem; color: #64748B; margin-top: 4px; white-space: nowrap; }

/* ── Payment Chart ── */
.rp-pay-list { display: flex; flex-direction: column; gap: 0.6rem; }
.rp-pay-item { display: flex; align-items: center; gap: 0.6rem; }
.rp-pay-info { display: flex; align-items: center; gap: 0.4rem; min-width: 140px; }
.rp-pay-icon { font-size: 1.2rem; }
.rp-pay-name { font-size: 0.8rem; font-weight: 600; color: #0F172A; }
.rp-pay-detail { font-size: 0.7rem; color: #64748B; }
.rp-pay-bar-wrap { flex: 1; background: #F1F5F9; border-radius: 4px; height: 10px; overflow: hidden; }
.rp-pay-bar { height: 100%; border-radius: 4px; transition: width 0.5s ease; }
.rp-pay-pct { font-size: 0.75rem; font-weight: 700; color: #0F172A; min-width: 40px; text-align: right; }

/* ── Table ── */
.rp-table-wrap { overflow-x: auto; }
.rp-table { width: 100%; border-collapse: collapse; font-size: 0.8rem; }
.rp-table th { padding: 0.5rem 0.75rem; text-align: left; font-weight: 600; color: #64748B; font-size: 0.7rem; text-transform: uppercase; letter-spacing: 0.3px; background: #F8FAFC; border-bottom: 1px solid #E2E8F0; }
.rp-table td { padding: 0.5rem 0.75rem; border-bottom: 1px solid #F1F5F9; color: #334155; }
.rp-table tr:hover { background: #F8FAFC; }
.rp-rank { font-weight: 700; color: #3B82F6; width: 30px; }
.rp-name { font-weight: 600; color: #0F172A; max-width: 200px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.rp-rev { font-weight: 700; color: #0F172A; white-space: nowrap; }
.rp-muted { color: #94A3B8; }

/* ── Badges ── */
.rp-badge { display: inline-block; padding: 0.15rem 0.5rem; border-radius: 10px; font-size: 0.7rem; font-weight: 600; }
.rp-badge.green { background: #ECFDF5; color: #059669; }
.rp-badge.blue { background: #EFF6FF; color: #2563EB; }
.rp-badge.orange { background: #FFF7ED; color: #EA580C; }
.rp-badge.red { background: #FEF2F2; color: #DC2626; }
.rp-badge.gray { background: #F1F5F9; color: #64748B; }
.rp-status { display: inline-block; padding: 0.15rem 0.5rem; border-radius: 10px; font-size: 0.7rem; font-weight: 600; }
.rp-status.success { background: #ECFDF5; color: #059669; }
.rp-status.warning { background: #FFF7ED; color: #EA580C; }
.rp-status.danger { background: #FEF2F2; color: #DC2626; }
.rp-stock-val { font-weight: 700; }
.rp-stock-val.danger { color: #DC2626; }
.rp-stock-val.warning { color: #EA580C; }

/* ── Mini Stats ── */
.rp-mini-stats { display: grid; grid-template-columns: repeat(auto-fit, minmax(130px, 1fr)); gap: 0.75rem; margin-bottom: 1rem; }
.rp-mini-stat { background: #fff; border: 1px solid #E2E8F0; border-radius: 0.6rem; padding: 0.8rem; text-align: center; }
.rp-mini-val { font-size: 1.3rem; font-weight: 700; color: #0F172A; margin: 0.2rem 0; }
.rp-mini-label { font-size: 0.7rem; color: #64748B; }

/* ── Row Layout ── */
.rp-row-2 { display: grid; grid-template-columns: 1fr 1fr; gap: 1rem; margin-bottom: 1rem; }
@media (max-width: 800px) { .rp-row-2 { grid-template-columns: 1fr; } }

/* ── Team ── */
.rp-team-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(280px, 1fr)); gap: 0.75rem; }
.rp-team-card { background: #fff; border: 1px solid #E2E8F0; border-radius: 0.75rem; padding: 1rem; }
.rp-team-head { display: flex; justify-content: space-between; align-items: center; margin-bottom: 0.75rem; }
.rp-team-head h6 { margin: 0; font-size: 0.9rem; display: flex; align-items: center; gap: 0.3rem; }
.rp-team-stats { display: flex; gap: 1rem; margin-bottom: 0.75rem; }
.rp-team-stat { text-align: center; }
.rp-team-num { font-size: 1rem; font-weight: 700; color: #0F172A; }
.rp-team-lbl { font-size: 0.65rem; color: #64748B; text-transform: uppercase; }
.rp-progress { }
.rp-progress-info { display: flex; justify-content: space-between; font-size: 0.7rem; color: #64748B; margin-bottom: 0.3rem; }
.rp-progress-bar { height: 8px; background: #F1F5F9; border-radius: 8px; overflow: hidden; }
.rp-progress-fill { height: 100%; border-radius: 8px; transition: width 0.5s; }
.rp-progress-fill.blue { background: linear-gradient(90deg, #3B82F6, #60A5FA); }
.rp-progress-fill.green { background: linear-gradient(90deg, #10B981, #34D399); }

/* ── ABC Analysis ── */
.rp-abc-summary { background: #fff; border: 1px solid #E2E8F0; border-radius: 0.75rem; padding: 1rem; margin-bottom: 1rem; }
.rp-abc-summary h6 { margin: 0 0 0.75rem; font-size: 0.85rem; display: flex; align-items: center; gap: 0.4rem; }
.rp-abc-cards { display: grid; grid-template-columns: repeat(3, 1fr); gap: 0.75rem; }
.rp-abc-card { border-left: 4px solid #ccc; padding: 0.6rem 0.8rem; background: #F8FAFC; border-radius: 0 0.4rem 0.4rem 0; }
.rp-abc-cat { font-size: 1.4rem; font-weight: 800; }
.rp-abc-label { font-size: 0.8rem; font-weight: 600; color: #0F172A; }
.rp-abc-count { font-size: 0.75rem; color: #64748B; }
.rp-abc-pct { font-size: 0.85rem; font-weight: 700; color: #0F172A; }
.rp-abc-desc { font-size: 0.65rem; color: #94A3B8; }
@media (max-width: 600px) { .rp-abc-cards { grid-template-columns: 1fr; } }

/* ── Categories ── */
.rp-cat-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(180px, 1fr)); gap: 0.75rem; }
.rp-cat-card { background: #fff; border: 1px solid #E2E8F0; border-radius: 0.75rem; padding: 1rem; text-align: center; }
.rp-cat-icon { font-size: 2rem; margin-bottom: 0.3rem; }
.rp-cat-name { font-weight: 700; font-size: 0.9rem; color: #0F172A; margin-bottom: 0.5rem; }
.rp-cat-stats { font-size: 0.75rem; color: #64748B; display: flex; flex-direction: column; gap: 0.2rem; }
.rp-cat-stats strong { color: #0F172A; }

/* ── Finances ── */
.rp-finance-summary { background: #fff; border: 1px solid #E2E8F0; border-radius: 0.75rem; padding: 1.2rem; margin-bottom: 1rem; }
.rp-finance-row { display: flex; justify-content: space-between; align-items: center; padding: 0.6rem 0; }
.rp-finance-label { display: flex; align-items: center; gap: 0.5rem; font-size: 0.9rem; font-weight: 500; }
.rp-finance-value { font-size: 1.2rem; font-weight: 700; }
.rp-finance-row.income .rp-finance-value { color: #3B82F6; }
.rp-finance-row.expense .rp-finance-value { color: #EF4444; }
.rp-finance-row.profit .rp-finance-value { color: #059669; }
.rp-finance-row.loss .rp-finance-value { color: #DC2626; }
.rp-finance-divider { border-top: 2px dashed #E2E8F0; margin: 0.3rem 0; }
.rp-finance-margin { text-align: right; font-size: 0.8rem; color: #64748B; padding-top: 0.3rem; }
.rp-finance-growth { display: flex; align-items: center; gap: 0.5rem; padding: 0.8rem 1rem; border-radius: 0.6rem; margin-bottom: 0.75rem; font-size: 0.85rem; font-weight: 600; }
.rp-finance-growth.pos { background: #ECFDF5; color: #059669; }
.rp-finance-growth.neg { background: #FEF2F2; color: #DC2626; }
.rp-finance-prev { font-weight: 400; font-size: 0.75rem; opacity: 0.8; }
.rp-finance-credit { display: flex; align-items: center; gap: 0.5rem; padding: 0.8rem 1rem; background: #FFFBEB; border-radius: 0.6rem; font-size: 0.85rem; color: #92400E; }

/* ── Stock Movements ── */
.rp-mov-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(150px, 1fr)); gap: 0.75rem; }
.rp-mov-item { text-align: center; padding: 0.6rem; background: #F8FAFC; border-radius: 0.5rem; }
.rp-mov-type { font-weight: 600; font-size: 0.8rem; margin-bottom: 0.3rem; }
.rp-mov-count { font-size: 0.75rem; color: #64748B; }
.rp-mov-qty { font-size: 1rem; font-weight: 700; color: #0F172A; }

/* ── Alert Banner ── */
.rp-alert-banner { display: flex; align-items: center; gap: 0.5rem; padding: 0.7rem 1rem; background: #FFF7ED; border: 1px solid #FDBA74; border-radius: 0.5rem; font-size: 0.8rem; color: #9A3412; margin-top: 0.5rem; }
.rp-alert-error { display: flex; justify-content: space-between; align-items: center; padding: 0.7rem 1rem; background: #FEF2F2; border: 1px solid #FECACA; border-radius: 0.5rem; color: #DC2626; font-size: 0.8rem; margin-bottom: 0.75rem; }
.rp-alert-error button { background: none; border: none; color: #DC2626; font-size: 1.1rem; cursor: pointer; }

/* ── Loading / Empty ── */
.rp-loading { display: flex; flex-direction: column; align-items: center; padding: 3rem; color: #64748B; }
.rp-spinner { width: 36px; height: 36px; border: 3px solid #E2E8F0; border-top-color: #3B82F6; border-radius: 50%; animation: rpSpin 0.7s linear infinite; }
@keyframes rpSpin { to { transform: rotate(360deg); } }
.rp-spin { animation: rpSpin 0.7s linear infinite; }
.rp-empty { display: flex; flex-direction: column; align-items: center; padding: 2rem; color: #CBD5E1; gap: 0.5rem; }
.rp-empty p { font-size: 0.8rem; margin: 0; }

/* ── Mobile ── */
@media (max-width: 600px) {
  .rp-page { padding: 0.5rem; }
  .rp-header h3 { font-size: 1.1rem; }
  .rp-stats-grid { grid-template-columns: 1fr 1fr; }
  .rp-stat-value { font-size: 1.1rem; }
  .rp-stat-icon { width: 38px; height: 38px; }
  .rp-stat-icon svg { width: 20px; height: 20px; }
  .rp-tabs { gap: 0; }
  .rp-tab { padding: 0.5rem 0.5rem; font-size: 0.7rem; }
  .rp-bar-chart { height: 160px; }
  .rp-team-grid { grid-template-columns: 1fr; }
}
`;

const ReportingPage = () => {

  // ── Formatage ──
  const fCurrency = (n) => {
    if (!n && n !== 0) return '0 FCFA';
    return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'XOF', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(n);
  };
  const fNum = (n) => {
    if (!n && n !== 0) return '0';
    if (n >= 1000000) return `${(n / 1000000).toFixed(1)}M`;
    if (n >= 1000) return `${(n / 1000).toFixed(1)}k`;
    return n.toLocaleString('fr-FR');
  };
  const fPct = (v, sign = true) => {
    if (!v && v !== 0) return '0%';
    return `${sign && v > 0 ? '+' : ''}${v.toFixed(1)}%`;
  };
  const fShortDate = (d) => {
    if (!d) return '';
    try { return new Date(d).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' }); }
    catch { return d; }
  };
  const fDate = (d) => {
    if (!d) return '—';
    try { return new Date(d).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' }); }
    catch { return d; }
  };

  // ── États ──
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('dashboard');
  const [selectedPeriod, setSelectedPeriod] = useState('today');
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');

  const emptyData = {
    summary: { revenue: 0, salesCount: 0, averageBasket: 0, revenueGrowth: 0, salesGrowth: 0, expenses: 0, netProfit: 0, profitMargin: 0, creditBalance: 0 },
    revenueTimeline: [],
    products: { topSelling: [], abcSummary: {}, abcDetails: [], neverSold: [], neverSoldCount: 0 },
    customers: { stats: {}, topByRevenue: [], creditDebtors: [] },
    payments: [],
    team: [],
    stock: { alerts: [], alertCount: 0, movements: {}, lowStockCount: 0, outOfStockCount: 0, totalQuantity: 0 },
    categories: [],
  };
  const [data, setData] = useState(emptyData);

  // ── Chargement ──
  const loadData = useCallback(async (period = selectedPeriod) => {
    setLoading(true);
    setError(null);
    try {
      const result = await reportService.getFullReport(
        period,
        period === 'custom' ? customStart : null,
        period === 'custom' ? customEnd : null
      );
      if (result.success && result.data) {
        setData(result.data);
      } else {
        setError('Erreur lors du chargement');
      }
    } catch (err) {
      console.error('Erreur:', err);
      setError('Erreur de connexion');
    } finally {
      setLoading(false);
    }
  }, [selectedPeriod, customStart, customEnd]);

  useEffect(() => { loadData(); }, []);

  const handlePeriodChange = (p) => {
    setSelectedPeriod(p);
    if (p !== 'custom') loadData(p);
  };

  const periodLabels = { today: "Aujourd'hui", week: 'Semaine', month: 'Mois', year: 'Année', custom: 'Personnalisé' };

  // ──────────────────────────────
  // COMPOSANTS
  // ──────────────────────────────

  const PeriodSelector = () => (
    <div className="rp-period-selector">
      <div className="rp-period-buttons">
        {['today', 'week', 'month', 'year'].map(p => (
          <button key={p} className={`rp-period-btn ${selectedPeriod === p ? 'active' : ''}`} onClick={() => handlePeriodChange(p)}>
            {periodLabels[p]}
          </button>
        ))}
        <button className={`rp-period-btn ${selectedPeriod === 'custom' ? 'active' : ''}`} onClick={() => handlePeriodChange('custom')}>
          <Calendar size={14} /> Personnalisé
        </button>
      </div>
      {selectedPeriod === 'custom' && (
        <div className="rp-custom-range">
          <input type="date" value={customStart} onChange={e => setCustomStart(e.target.value)} className="rp-date-input" />
          <span>→</span>
          <input type="date" value={customEnd} onChange={e => setCustomEnd(e.target.value)} className="rp-date-input" />
          <button className="rp-btn-primary rp-btn-sm" onClick={() => loadData('custom')}>Rechercher</button>
        </div>
      )}
    </div>
  );

  // ── KPI Cards ──
  const SummaryCards = () => {
    const s = data.summary;
    const cards = [
      { title: "Chiffre d'Affaires", value: fCurrency(s.revenue), growth: s.revenueGrowth, icon: DollarSign, color: '#3B82F6' },
      { title: 'Ventes', value: fNum(s.salesCount), growth: s.salesGrowth, icon: ShoppingCart, color: '#10B981' },
      // { title: 'Panier Moyen', value: fCurrency(s.averageBasket), icon: Target, color: '#06B6D4' },
      { title: 'Profit Net', value: fCurrency(s.netProfit), sub: s.profitMargin ? `Marge: ${fPct(s.profitMargin, false)}` : null, icon: TrendingUp, color: s.netProfit >= 0 ? '#10B981' : '#EF4444' },
      { title: 'Dépenses', value: fCurrency(s.expenses), icon: ArrowDownRight, color: '#EF4444' },
      { title: 'Crédits en cours', value: fCurrency(s.creditBalance), sub: data.customers?.creditDebtors?.length ? `${data.customers.creditDebtors.length} débiteur(s)` : null, icon: CreditCard, color: '#F59E0B' },
    ];
    return (
      <div className="rp-stats-grid">
        {cards.map((c, i) => {
          const Icon = c.icon;
          const GIcon = c.growth ? (c.growth >= 0 ? TrendingUp : TrendingDown) : null;
          return (
            <div key={i} className="rp-stat-card">
              <div className="rp-stat-header">
                <div>
                  <div className="rp-stat-title">{c.title}</div>
                  <div className="rp-stat-value">{c.value}</div>
                  {c.growth != null && c.growth !== 0 && (
                    <div className={`rp-stat-growth ${c.growth >= 0 ? 'pos' : 'neg'}`}>
                      {GIcon && <GIcon size={13} />} {fPct(c.growth)} vs préc.
                    </div>
                  )}
                  {c.sub && <div className="rp-stat-sub">{c.sub}</div>}
                </div>
                <div className="rp-stat-icon" style={{ backgroundColor: `${c.color}18` }}>
                  <Icon size={28} style={{ color: c.color }} />
                </div>
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  // ── Graphique Barres Revenus ──
  const RevenueChart = () => {
    const items = data.revenueTimeline || [];
    const maxRev = items.length > 0 ? Math.max(...items.map(d => d.revenue)) : 0;
    return (
      <div className="rp-card">
        <div className="rp-card-head">
          <h6><BarChart3 size={16} style={{ color: '#3B82F6' }} /> Évolution Revenus — {periodLabels[selectedPeriod]}</h6>
          {items.length > 0 && <span className="rp-card-total">Total: {fCurrency(items.reduce((s, d) => s + d.revenue, 0))}</span>}
        </div>
        <div className="rp-card-body">
          {items.length > 0 ? (
            <div className="rp-bar-chart">
              {items.map((item, i) => (
                <div key={i} className="rp-bar-col">
                  <div className="rp-bar-val">{fCurrency(item.revenue)}</div>
                  <div className="rp-bar-wrap">
                    <div className="rp-bar-fill" style={{
                      height: `${maxRev > 0 ? (item.revenue / maxRev) * 100 : 0}%`,
                      background: item.revenue === maxRev ? '#3B82F6' : '#93C5FD'
                    }}>
                      {item.salesCount > 0 && <span className="rp-bar-count">{item.salesCount}v</span>}
                    </div>
                  </div>
                  <div className="rp-bar-label">{fShortDate(item.date)}</div>
                </div>
              ))}
            </div>
          ) : (
            <div className="rp-empty"><BarChart3 size={40} /><p>Aucune donnée pour cette période</p></div>
          )}
        </div>
      </div>
    );
  };

  // ── Paiements ──
  const PaymentChart = () => {
    const colors = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#06B6D4', '#EC4899'];
    return (
      <div className="rp-card">
        <div className="rp-card-head"><h6><PieChart size={16} style={{ color: '#10B981' }} /> Modes de Paiement</h6></div>
        <div className="rp-card-body">
          {data.payments.length > 0 ? (
            <div className="rp-pay-list">
              {data.payments.map((m, i) => (
                <div key={i} className="rp-pay-item">
                  <div className="rp-pay-info">
                    <span className="rp-pay-icon">{m.icon}</span>
                    <div>
                      <div className="rp-pay-name">{m.label}</div>
                      <div className="rp-pay-detail">{m.count} trans. · {fCurrency(m.amount)}</div>
                    </div>
                  </div>
                  <div className="rp-pay-bar-wrap">
                    <div className="rp-pay-bar" style={{ width: `${m.percent}%`, background: colors[i % colors.length] }} />
                  </div>
                  <div className="rp-pay-pct">{m.percent.toFixed(1)}%</div>
                </div>
              ))}
            </div>
          ) : (
            <div className="rp-empty"><PieChart size={40} /><p>Aucune donnée</p></div>
          )}
        </div>
      </div>
    );
  };

  // ── Table générique ──
  const DataTable = ({ icon, iconColor, title, headers, rows, emptyMsg }) => (
    <div className="rp-card">
      <div className="rp-card-head"><h6>{icon && React.createElement(icon, { size: 16, style: { color: iconColor } })} {title}</h6></div>
      <div className="rp-table-wrap">
        {rows.length > 0 ? (
          <table className="rp-table">
            <thead><tr>{headers.map((h, i) => <th key={i}>{h}</th>)}</tr></thead>
            <tbody>{rows}</tbody>
          </table>
        ) : (
          <div className="rp-empty">{icon && React.createElement(icon, { size: 32 })}<p>{emptyMsg || 'Aucune donnée'}</p></div>
        )}
      </div>
    </div>
  );

  // ──────────────────────────────
  // ONGLET 1 : Dashboard
  // ──────────────────────────────
  const DashboardTab = () => (
    <div>
      <SummaryCards />
      <div className="rp-row-2">
        <RevenueChart />
        <PaymentChart />
      </div>
      <div className="rp-row-2">
        <DataTable icon={Award} iconColor="#F59E0B" title="Top Produits"
          headers={['#', 'Produit', 'Vendus', 'Revenus']}
          rows={data.products.topSelling.slice(0, 5).map((p, i) => (
            <tr key={i}><td className="rp-rank">{i + 1}</td><td className="rp-name">{p.productName}</td>
              <td><span className="rp-badge green">{fNum(p.totalSold)}</span></td><td className="rp-rev">{fCurrency(p.totalRevenue)}</td></tr>
          ))} emptyMsg="Aucune vente" />
        <DataTable icon={Users} iconColor="#06B6D4" title="Top Clients"
          headers={['#', 'Client', 'Cmd', 'Total']}
          rows={data.customers.topByRevenue.slice(0, 5).map((c, i) => (
            <tr key={i}><td className="rp-rank">{i + 1}</td><td className="rp-name">{c.customerName}</td>
              <td><span className="rp-badge blue">{c.orderCount}</span></td><td className="rp-rev">{fCurrency(c.totalSpent)}</td></tr>
          ))} emptyMsg="Aucun client" />
      </div>
      {data.stock.alertCount > 0 && (
        <div className="rp-alert-banner">
          <AlertTriangle size={16} /> <strong>{data.stock.alertCount} alerte(s) stock</strong> — {data.stock.outOfStockCount} rupture(s)
          <button className="rp-link-btn" onClick={() => setActiveTab('stock')}>Voir →</button>
        </div>
      )}
    </div>
  );

  // ──────────────────────────────
  // ONGLET 2 : Produits + ABC
  // ──────────────────────────────
  const ProductsTab = () => {
    const abc = data.products.abcSummary;
    return (
      <div>
        {/* ABC Summary */}
        {abc && abc.A && (
          <div className="rp-abc-summary">
            <h6><Layers size={16} style={{ color: '#8B5CF6' }} /> Analyse ABC (Pareto)</h6>
            <div className="rp-abc-cards">
              {[
                { cat: 'A', label: 'Stars', color: '#10B981', desc: '≈80% du CA' },
                { cat: 'B', label: 'Réguliers', color: '#F59E0B', desc: '≈15% du CA' },
                { cat: 'C', label: 'Queue longue', color: '#EF4444', desc: '≈5% du CA' },
              ].map(({ cat, label, color, desc }) => (
                <div key={cat} className="rp-abc-card" style={{ borderLeftColor: color }}>
                  <div className="rp-abc-cat" style={{ color }}>{cat}</div>
                  <div className="rp-abc-label">{label}</div>
                  <div className="rp-abc-count">{abc[cat]?.count || 0} produits</div>
                  <div className="rp-abc-pct">{abc[cat]?.revenuePercent || 0}% CA</div>
                  <div className="rp-abc-desc">{desc}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="rp-row-2">
          <DataTable icon={Award} iconColor="#F59E0B" title="Top Ventes"
            headers={['#', 'Produit', 'Catégorie', 'Vendus', 'Stock']}
            rows={data.products.topSelling.slice(0, 10).map((p, i) => (
              <tr key={i}><td className="rp-rank">{i + 1}</td><td className="rp-name">{p.productName}</td>
                <td><span className="rp-badge blue">{p.categoryName}</span></td>
                <td><span className="rp-badge green">{fNum(p.totalSold)}</span></td>
                <td><span className={`rp-badge ${p.currentStock <= 10 ? 'red' : 'gray'}`}>{p.currentStock}</span></td></tr>
            ))} emptyMsg="Aucune vente" />
          <DataTable icon={DollarSign} iconColor="#10B981" title="Top Revenus"
            headers={['#', 'Produit', 'Revenus', 'Vendus']}
            rows={[...data.products.topSelling].sort((a, b) => b.totalRevenue - a.totalRevenue).slice(0, 10).map((p, i) => (
              <tr key={i}><td className="rp-rank">{i + 1}</td><td className="rp-name">{p.productName}</td>
                <td className="rp-rev">{fCurrency(p.totalRevenue)}</td>
                <td><span className="rp-badge green">{fNum(p.totalSold)}</span></td></tr>
            ))} emptyMsg="Aucun revenu" />
        </div>

        {/* Produits jamais vendus */}
        {data.products.neverSoldCount > 0 && (
          <DataTable icon={Package} iconColor="#EF4444" title={`Produits jamais vendus (${data.products.neverSoldCount})`}
            headers={['Produit', 'Prix', 'Ajouté le']}
            rows={data.products.neverSold.slice(0, 15).map((p, i) => (
              <tr key={i}><td className="rp-name">{p.productName}</td><td>{fCurrency(p.price)}</td><td>{fDate(p.createdAt)}</td></tr>
            ))} emptyMsg="" />
        )}
      </div>
    );
  };

  // ──────────────────────────────
  // ONGLET 3 : Clients
  // ──────────────────────────────
  const CustomersTab = () => {
    const s = data.customers.stats;
    return (
      <div>
        <div className="rp-mini-stats">
          {[
            { label: 'Total clients', value: fNum(s.total || 0), icon: Users, color: '#3B82F6' },
            { label: 'VIP', value: fNum(s.vipCount || 0), icon: Award, color: '#F59E0B' },
            { label: 'Revendeurs', value: fNum(s.resellerCount || 0), icon: ShoppingCart, color: '#8B5CF6' },
            { label: 'Avec achats', value: fNum(s.withPurchases || 0), icon: Target, color: '#10B981' },
          ].map((item, i) => (
            <div key={i} className="rp-mini-stat">
              <item.icon size={18} style={{ color: item.color }} />
              <div className="rp-mini-val">{item.value}</div>
              <div className="rp-mini-label">{item.label}</div>
            </div>
          ))}
        </div>
        <div className="rp-row-2">
          <DataTable icon={Award} iconColor="#10B981" title="Top Clients par CA"
            headers={['#', 'Client', 'Commandes', 'Total', 'Dernier achat']}
            rows={data.customers.topByRevenue.map((c, i) => (
              <tr key={i}><td className="rp-rank">{i + 1}</td><td className="rp-name">{c.customerName}</td>
                <td><span className="rp-badge blue">{c.orderCount}</span></td>
                <td className="rp-rev">{fCurrency(c.totalSpent)}</td><td>{fDate(c.lastPurchase)}</td></tr>
            ))} emptyMsg="Aucun client" />
          <DataTable icon={CreditCard} iconColor="#EF4444" title="Débiteurs Crédit"
            headers={['Client', 'Crédits', 'Solde dû', 'Plus ancien']}
            rows={data.customers.creditDebtors.map((c, i) => (
              <tr key={i}><td className="rp-name">{c.customerName}</td>
                <td><span className="rp-badge orange">{c.creditCount}</span></td>
                <td className="rp-rev" style={{ color: '#EF4444' }}>{fCurrency(c.balance)}</td>
                <td>{fDate(c.oldestCredit)}</td></tr>
            ))} emptyMsg="Aucun crédit en cours" />
        </div>
      </div>
    );
  };

  // ──────────────────────────────
  // ONGLET 4 : Paiements
  // ──────────────────────────────
  const PaymentsTab = () => (
    <div>
      <PaymentChart />
      <DataTable icon={DollarSign} iconColor="#10B981" title="Détail Méthodes de Paiement"
        headers={['', 'Méthode', 'Transactions', 'Montant', '%']}
        rows={data.payments.map((p, i) => (
          <tr key={i}><td>{p.icon}</td><td className="rp-name">{p.label}</td>
            <td><span className="rp-badge blue">{p.count}</span></td>
            <td className="rp-rev">{fCurrency(p.amount)}</td>
            <td><strong>{p.percent.toFixed(1)}%</strong></td></tr>
        ))} emptyMsg="Aucune transaction" />
    </div>
  );

  // ──────────────────────────────
  // ONGLET 5 : Équipe
  // ──────────────────────────────
  const TeamTab = () => (
    <div className="rp-team-grid">
      {data.team.length > 0 ? data.team.map((s, i) => (
        <div key={i} className="rp-team-card">
          <div className="rp-team-head">
            <h6><Zap size={16} style={{ color: '#3B82F6' }} /> {s.name}</h6>
            <span className={`rp-status ${s.revenue >= s.target ? 'success' : 'warning'}`}>
              {s.revenue >= s.target ? '✓ Objectif' : 'En cours'}
            </span>
          </div>
          <div className="rp-team-stats">
            <div className="rp-team-stat"><div className="rp-team-num">{s.salesCount}</div><div className="rp-team-lbl">Ventes</div></div>
            <div className="rp-team-stat"><div className="rp-team-num">{fCurrency(s.revenue)}</div><div className="rp-team-lbl">CA</div></div>
            <div className="rp-team-stat"><div className="rp-team-num">{fCurrency(s.target)}</div><div className="rp-team-lbl">Objectif</div></div>
          </div>
          <div className="rp-progress">
            <div className="rp-progress-info"><span>Progression</span><span>{s.progressPercent.toFixed(1)}%</span></div>
            <div className="rp-progress-bar"><div className={`rp-progress-fill ${s.revenue >= s.target ? 'green' : 'blue'}`} style={{ width: `${Math.min(s.progressPercent, 100)}%` }} /></div>
          </div>
        </div>
      )) : (
        <div className="rp-empty"><Zap size={48} /><p>Aucune donnée d'équipe</p></div>
      )}
    </div>
  );

  // ──────────────────────────────
  // ONGLET 6 : Stock
  // ──────────────────────────────
  const StockTab = () => {
    const st = data.stock;
    const mov = st.movements || {};
    return (
      <div>
        <div className="rp-mini-stats">
          {[
            { label: 'Alertes', value: st.alertCount, icon: AlertTriangle, color: '#F59E0B' },
            { label: 'Ruptures', value: st.outOfStockCount, icon: Package, color: '#EF4444' },
            { label: 'Stock bas', value: st.lowStockCount, icon: TrendingDown, color: '#F97316' },
            { label: 'Qté totale', value: fNum(st.totalQuantity), icon: Layers, color: '#3B82F6' },
          ].map((item, i) => (
            <div key={i} className="rp-mini-stat">
              <item.icon size={18} style={{ color: item.color }} />
              <div className="rp-mini-val">{item.value}</div>
              <div className="rp-mini-label">{item.label}</div>
            </div>
          ))}
        </div>

        {/* Mouvements */}
        {Object.keys(mov).length > 0 && (
          <div className="rp-card" style={{ marginBottom: '1rem' }}>
            <div className="rp-card-head"><h6><ArrowUpRight size={16} style={{ color: '#3B82F6' }} /> Mouvements de Stock</h6></div>
            <div className="rp-card-body">
              <div className="rp-mov-grid">
                {Object.entries(mov).map(([type, d]) => (
                  <div key={type} className="rp-mov-item">
                    <div className="rp-mov-type">{type === 'IN' ? '📥 Entrées' : type === 'OUT' ? '📤 Sorties' : type === 'ADJUSTMENT' ? '⚙️ Ajustements' : `📦 ${type}`}</div>
                    <div className="rp-mov-count">{d.count} mvt(s)</div>
                    <div className="rp-mov-qty">{fNum(d.totalQuantity)} unités</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        <DataTable icon={AlertTriangle} iconColor="#F59E0B" title={`Alertes Stock (${st.alertCount})`}
          headers={['Produit', 'Catégorie', 'Stock', 'Seuil', 'Status']}
          rows={(st.alerts || []).map((a, i) => (
            <tr key={i}><td className="rp-name">{a.productName}</td>
              <td><span className="rp-badge blue">{a.categoryName}</span></td>
              <td><span className={`rp-stock-val ${a.currentStock === 0 ? 'danger' : 'warning'}`}>{a.currentStock}</span></td>
              <td className="rp-muted">{a.minThreshold}</td>
              <td><span className={`rp-status ${a.status === 'CRITICAL' ? 'danger' : 'warning'}`}>{a.status === 'CRITICAL' ? 'Rupture' : 'Bas'}</span></td></tr>
          ))} emptyMsg="Aucune alerte stock" />
      </div>
    );
  };

  // ──────────────────────────────
  // ONGLET 7 : Catégories
  // ──────────────────────────────
  const CategoriesTab = () => (
    <div>
      {data.categories.length > 0 ? (
        <div className="rp-cat-grid">
          {data.categories.map((c, i) => (
            <div key={i} className="rp-cat-card">
              <div className="rp-cat-icon">{c.icon || '📦'}</div>
              <div className="rp-cat-name">{c.categoryName}</div>
              <div className="rp-cat-stats">
                <div><strong>{c.productCount}</strong> produits</div>
                <div><strong>{fNum(c.totalStock)}</strong> en stock</div>
                <div><strong>{fNum(c.monthlySales)}</strong> ventes/mois</div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="rp-empty"><Layers size={48} /><p>Aucune catégorie</p></div>
      )}
    </div>
  );

  // ──────────────────────────────
  // ONGLET 8 : Finances
  // ──────────────────────────────
  const FinancesTab = () => {
    const s = data.summary;
    return (
      <div>
        <div className="rp-finance-summary">
          <div className="rp-finance-row income">
            <div className="rp-finance-label"><DollarSign size={18} /> Chiffre d'Affaires</div>
            <div className="rp-finance-value">{fCurrency(s.revenue)}</div>
          </div>
          <div className="rp-finance-row expense">
            <div className="rp-finance-label"><ArrowDownRight size={18} /> Dépenses</div>
            <div className="rp-finance-value">- {fCurrency(s.expenses)}</div>
          </div>
          <div className="rp-finance-divider" />
          <div className={`rp-finance-row ${s.netProfit >= 0 ? 'profit' : 'loss'}`}>
            <div className="rp-finance-label"><TrendingUp size={18} /> Profit Net</div>
            <div className="rp-finance-value">{fCurrency(s.netProfit)}</div>
          </div>
          <div className="rp-finance-margin">Marge: <strong>{fPct(s.profitMargin, false)}</strong></div>
        </div>

        {s.revenueGrowth !== 0 && (
          <div className={`rp-finance-growth ${s.revenueGrowth >= 0 ? 'pos' : 'neg'}`}>
            {s.revenueGrowth >= 0 ? <TrendingUp size={18} /> : <TrendingDown size={18} />}
            <span>{fPct(s.revenueGrowth)} vs période précédente</span>
            <span className="rp-finance-prev">(avant: {fCurrency(s.previousRevenue)})</span>
          </div>
        )}

        {s.creditBalance > 0 && (
          <div className="rp-finance-credit">
            <CreditCard size={18} />
            <span>Crédits en cours : <strong>{fCurrency(s.creditBalance)}</strong></span>
            {data.customers.creditDebtors.length > 0 && (
              <span> — {data.customers.creditDebtors.length} débiteur(s)</span>
            )}
          </div>
        )}
      </div>
    );
  };

  // ── Tabs config ──
  const tabs = [
    { id: 'dashboard', label: 'Dashboard', icon: TrendingUp },
    { id: 'products', label: 'Produits', icon: Package, count: data.products.topSelling.length },
    { id: 'customers', label: 'Clients', icon: Users },
    { id: 'payments', label: 'Paiements', icon: DollarSign },
    { id: 'team', label: 'Équipe', icon: Zap, count: data.team.length },
    { id: 'stock', label: 'Stock', icon: AlertTriangle, badge: data.stock.alertCount },
    { id: 'categories', label: 'Catégories', icon: Layers, count: data.categories.length },
    { id: 'finances', label: 'Finances', icon: BarChart3 },
  ];

  // ══════════════════════════════
  // RENDU
  // ══════════════════════════════
  return (
    <div className="rp-page">
      <style>{rpStyles}</style>

      <div className="rp-header">
        <div>
          <h3>Analytics & Reporting</h3>
          <p className="rp-subtitle">Performances de votre boutique</p>
        </div>
        <div className="rp-actions">
          <button className="rp-btn-outline" onClick={() => loadData()} disabled={loading}>
            <RefreshCw size={15} className={loading ? 'rp-spin' : ''} /> Actualiser
          </button>
        </div>
      </div>

      <PeriodSelector />

      {error && (
        <div className="rp-alert-error">
          <span>{error}</span>
          <button onClick={() => setError(null)}>×</button>
        </div>
      )}

      {loading ? (
        <div className="rp-loading"><div className="rp-spinner" /><p>Chargement...</p></div>
      ) : (
        <>
          <div className="rp-tabs">
            {tabs.map(t => (
              <button key={t.id} className={`rp-tab ${activeTab === t.id ? 'active' : ''}`} onClick={() => setActiveTab(t.id)}>
                <t.icon size={15} /> {t.label}
                {t.badge > 0 && <span className="rp-tab-badge">{t.badge}</span>}
                {t.count > 0 && !t.badge && <span className="rp-tab-count">({t.count})</span>}
              </button>
            ))}
          </div>

          <div className="rp-tab-content">
            {activeTab === 'dashboard' && <DashboardTab />}
            {activeTab === 'products' && <ProductsTab />}
            {activeTab === 'customers' && <CustomersTab />}
            {activeTab === 'payments' && <PaymentsTab />}
            {activeTab === 'team' && <TeamTab />}
            {activeTab === 'stock' && <StockTab />}
            {activeTab === 'categories' && <CategoriesTab />}
            {activeTab === 'finances' && <FinancesTab />}
          </div>
        </>
      )}
    </div>
  );
};

export default ReportingPage;